import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import { getToolComponent } from './tools';
import ReactMarkdown from 'react-markdown';
import { parseTaskBlocks } from '../../utils/taskParser';

export interface Action {
  id: string;
  type: 'tool' | 'reasoning';
  toolName?: string;
  toolInput?: any;
  toolOutput?: any;
  toolIsError?: boolean;
  isStreaming?: boolean; // For incomplete tool blocks (e.g., shell_exec)
  reasoningContent?: string;
  description: string;
}

interface ActionGroupProps {
  actions: Action[];
  isStreaming?: boolean;
  currentStreamingAction?: string;
}

export const ActionGroup: React.FC<ActionGroupProps> = ({
  actions,
  isStreaming = false,
  currentStreamingAction
}) => {
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());

  const handleToolToggle = (toolId: string) => {
    setExpandedTools(prev => {
      const next = new Set(prev);
      if (next.has(toolId)) {
        next.delete(toolId);
      } else {
        next.add(toolId);
      }
      return next;
    });
  };

  // Get display items for the summary (can be string or JSX)
  const getSummaryItems = () => {
    const items: (string | React.ReactNode)[] = [];
    
    actions.forEach(action => {
      if (action.type === 'reasoning') {
        // Only show reasoning content if it has actual content and is complete
        if (action.reasoningContent?.trim() && !(isStreaming && action.id === currentStreamingAction)) {
          // Show the actual thinking content once complete (truncated to one line)
          const lines = action.reasoningContent.split('\n').filter(line => line.trim());
          let firstLine = lines[0] || '';
          // Remove trailing colon
          if (firstLine.endsWith(':')) {
            firstLine = firstLine.slice(0, -1);
          }
          items.push(firstLine);
        }
        // Don't show anything while streaming or if empty
      } else if (action.type === 'tool') {
        // Check for custom rendering for todos
        if (action.toolName === 'todos' && action.toolInput && action.toolOutput && !isStreaming) {
          const todoAction = action.toolInput.action;
          
          // Skip listing actions entirely - they're just internal state checks
          if (todoAction === 'list' || todoAction === 'list_incomplete') {
            return; // Don't add anything to items for list actions
          }
          
          if (todoAction === 'complete' || todoAction === 'cancel') {
            try {
              const output = typeof action.toolOutput === 'string' 
                ? action.toolOutput 
                : JSON.stringify(action.toolOutput);
              
              // Parse the task blocks to get structured data
              const parsedContent = parseTaskBlocks(output);
              
              // Find the tasks block
              const tasksBlock = parsedContent.find(block => block.type === 'tasks');
              if (tasksBlock && Array.isArray(tasksBlock.content) && tasksBlock.content.length > 0) {
                const task = tasksBlock.content[0]; // Get the first (and should be only) task
                
                if (todoAction === 'complete') {
                  items.push(
                    <span key={action.id} className="line-through text-muted-foreground">
                      {task.title}
                    </span>
                  );
                } else if (todoAction === 'cancel') {
                  items.push(
                    <span key={action.id} className="line-through text-muted-foreground/60">
                      {task.title}
                    </span>
                  );
                }
                return; // Skip default processing
              }
            } catch (e) {
              // Fall through to default
            }
          }
          // For other actions (append, insert, add_note), use default
          items.push(action.description);
        } else if (isStreaming && action.id === currentStreamingAction) {
          items.push(action.description || 'Processing...');
        } else {
          items.push(action.description);
        }
      }
    });
    
    // Limit to 5 items, showing "+N actions" for overflow
    if (items.length > 5) {
      const hiddenCount = items.length - 5;
      const visibleItems = items.slice(-5); // Keep the most recent 5
      visibleItems.unshift(
        <span key="overflow" className="text-muted-foreground/70">
          +{hiddenCount} {hiddenCount === 1 ? 'action' : 'actions'}
        </span>
      );
      return visibleItems;
    }
    
    return items;
  };

  // Always show expanded view
  return (
    <div>
      <div className="space-y-6">
        {actions.map((action) => {
          if (action.type === 'tool' && action.toolName) {
            const ToolComponent = getToolComponent(action.toolName);
            return (
              <div
                key={action.id}
                data-msg-id={action.id}
                onClick={(e) => e.stopPropagation()}
              >
                <ToolComponent
                  toolName={action.toolName}
                  input={action.toolInput}
                  output={action.toolOutput}
                  isError={action.toolIsError}
                  isExpanded={expandedTools.has(action.id)}
                  onToggle={() => handleToolToggle(action.id)}
                  isStreaming={action.isStreaming}
                />
              </div>
            );
          }

          if (action.type === 'reasoning' && action.reasoningContent?.trim()) {
            return (
              <div key={action.id} data-msg-id={action.id} className="text-xs text-gray-400">
                <ReactMarkdown>{action.reasoningContent}</ReactMarkdown>
              </div>
            );
          }

          return null;
        })}
      </div>
    </div>
  );
};